'use client'

import React from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { UtilityButton } from '../Button/UtilityButton'
import { ChevronLeftIcon } from '../Icon/ChevronLeftIcon'
import { ChevronRightIcon } from '../Icon/ChevronRightIcon'
import {
  bodyStyles,
  containerStyles,
  contentStyles,
  footerStyles,
  headerStyles,
  toggleButtonStyles,
  toggleWrapperStyles,
} from './SidePanel.styles'

export type SidePanelSize = 'sm' | 'md' | 'lg'

export type SidePanelProps = {
  children: React.ReactNode
  /** Design Library width tier: sm=400, md=480 (default), lg=560. */
  size?: SidePanelSize
  /**
   * Header block above the divider — close/back control, titles, and any
   * always-visible action. Stays fixed while the body scrolls.
   */
  header?: React.ReactNode
  /** Sticky footer below its own divider, for form actions. */
  footer?: React.ReactNode
  /**
   * Whether the panel is expanded. Content stays mounted (CSS-hidden) while
   * collapsed so the hosted view keeps its state across a collapse cycle.
   */
  isExpanded?: boolean
  /**
   * Collapse/expand handler. When provided, a chevron UtilityButton floats on
   * the panel's outer-left edge; omit it to render a plain docked panel.
   */
  onToggle?: () => void
  /** Accessible labels for the collapse toggle. */
  collapseLabel?: string
  expandLabel?: string
  colorScheme?: ColorScheme
  /**
   * Removes the body's 24px gutters for edge-to-edge rows. The body remains
   * the panel's single scroll region either way.
   */
  isBodyPadded?: boolean
  'data-testid'?: string
}

export const SidePanel = ({
  children,
  size = 'md',
  header,
  footer,
  isExpanded = true,
  onToggle,
  collapseLabel = 'Collapse panel',
  expandLabel = 'Expand panel',
  colorScheme = 'dark',
  isBodyPadded = true,
  'data-testid': dataTestId = 'SidePanel',
}: SidePanelProps) => (
  <Box
    aria-expanded={isExpanded}
    data-testid={dataTestId}
    {...containerStyles(colorScheme, size, isExpanded)}
  >
    {onToggle && (
      <Box {...toggleWrapperStyles}>
        {/* A chevron, not an arrow: collapsing a rail is a disclosure, while an
            arrow-left reads as "go back". Points outward (›) to push the panel
            closed and inward (‹) to pull it open, matching the other rails. */}
        <UtilityButton
          colorScheme={colorScheme}
          data-testid={`${dataTestId}:Toggle`}
          icon={
            isExpanded ? (
              <ChevronRightIcon height={20} width={20} />
            ) : (
              <ChevronLeftIcon height={20} width={20} />
            )
          }
          label={isExpanded ? collapseLabel : expandLabel}
          onClick={onToggle}
          stylesOverride={toggleButtonStyles(colorScheme)}
        />
      </Box>
    )}
    <Box {...contentStyles(isExpanded)}>
      {header && <Box {...headerStyles(colorScheme)}>{header}</Box>}
      <Box {...bodyStyles(isBodyPadded)}>{children}</Box>
      {footer && <Box {...footerStyles(colorScheme)}>{footer}</Box>}
    </Box>
  </Box>
)
